import React, { useState, useMemo } from 'react';
import { Plus, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import countryList from 'react-select-country-list';
import { FieldError, cls, getErr } from './helpers';
import { toast } from 'react-toastify';

const EMPTY = { diplome: '', autre: '', date_obtention: '', etablissement: '', niveau_etude: '', pays: 'Niger', domaine_formation: '' };

// Niveaux qui ne nécessitent pas de domaine de formation
const NIVEAUX_SANS_DOMAINE = ['primaire', 'secondaire 1er cycle', 'secondaire 2ème cycle', 'secondaire 2eme cycle'];

const StepDiplomes = ({ diplomes, onDiplomesChange, typesDiplome, niveauxEtude, domainesFormation, onNext, onBack }) => {
    const [form, setForm]           = useState(EMPTY);
    const [errors, setErrors]       = useState({});
    const [showOther, setShowOther] = useState(false);
    const countryOptions            = useMemo(() => countryList().getData(), []);

    // Détermine si le domaine doit être affiché selon le niveau sélectionné
    const showDomaine = useMemo(() => {
        if (!form.niveau_etude) return false;
        const niveau = niveauxEtude.find(n => String(n.id) === String(form.niveau_etude))?.niveau || '';
        return !NIVEAUX_SANS_DOMAINE.some(n => niveau.toLowerCase().includes(n.toLowerCase()));
    }, [form.niveau_etude, niveauxEtude]);

    const validate = () => {
        const e = {};
        if (!form.diplome)                    e.diplome        = "Le type de diplôme est requis";
        if (showOther && !form.autre?.trim()) e.autre          = "Précisez le diplôme";
        if (!form.date_obtention)             e.date_obtention = "La date d'obtention est requise";
        if (!form.etablissement.trim())       e.etablissement  = "L'établissement est requis";
        if (!form.niveau_etude)               e.niveau_etude   = "Le niveau d'études est requis";
        if (showDomaine && !form.domaine_formation) e.domaine_formation = "Le domaine est requis pour ce niveau";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const addDiplome = () => {
        if (!validate()) return;
        const typeName = showOther
            ? form.autre
            : (typesDiplome.find(t => String(t.id) === String(form.diplome))?.nom || form.diplome);
        onDiplomesChange([...diplomes, { ...form, _typeName: typeName }]);
        setForm(EMPTY);
        setShowOther(false);
        setErrors({});
    };

    const handleNext = () => {
        if (diplomes.length === 0) { toast.error("Ajoutez au moins un diplôme"); return; }
        onNext();
    };

    return (
        <div className="space-y-4">
            <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                <h4 className="text-sm font-semibold text-orange-700 flex items-center gap-1"><Plus className="h-4 w-4" /> Ajouter un diplôme</h4>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Type <span className="text-red-500">*</span></label>
                        <select value={form.diplome} onChange={e => { const v = e.target.value; setForm(p => ({...p, diplome: v})); setShowOther(v === 'autre'); }} className={cls(errors.diplome, false)}>
                            <option value="">Sélectionnez</option>
                            {typesDiplome.map(t => <option key={t.id} value={t.id}>{t.nom}</option>)}
                            <option value="autre">Autre...</option>
                        </select>
                        <FieldError msg={getErr(errors, 'diplome')} />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Niveau d'études <span className="text-red-500">*</span></label>
                        <select value={form.niveau_etude} onChange={e => setForm(p => ({...p, niveau_etude: e.target.value, domaine_formation: ''}))} className={cls(errors.niveau_etude, false)}>
                            <option value="">Sélectionnez</option>
                            {niveauxEtude.map(n => <option key={n.id} value={n.id}>{n.niveau}</option>)}
                        </select>
                        <FieldError msg={getErr(errors, 'niveau_etude')} />
                    </div>
                </div>
                {showDomaine && (
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Domaine de formation <span className="text-red-500">*</span></label>
                        <select value={form.domaine_formation} onChange={e => setForm(p => ({...p, domaine_formation: e.target.value}))} className={cls(errors.domaine_formation, false)}>
                            <option value="">Sélectionnez</option>
                            {domainesFormation?.map(d => <option key={d.id} value={d.id}>{d.nom}</option>)}
                        </select>
                        <FieldError msg={getErr(errors, 'domaine_formation')} />
                    </div>
                )}
                {showOther && (
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Précisez <span className="text-red-500">*</span></label>
                        <input type="text" value={form.autre} onChange={e => setForm(p => ({...p, autre: e.target.value}))} className={cls(errors.autre, false)} placeholder="Nom du diplôme" />
                        <FieldError msg={getErr(errors, 'autre')} />
                    </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Établissement <span className="text-red-500">*</span></label>
                        <input type="text" value={form.etablissement} onChange={e => setForm(p => ({...p, etablissement: e.target.value}))} className={cls(errors.etablissement, false)} placeholder="École / Université" />
                        <FieldError msg={getErr(errors, 'etablissement')} />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Date d'obtention <span className="text-red-500">*</span></label>
                        <input type="date" value={form.date_obtention} onChange={e => setForm(p => ({...p, date_obtention: e.target.value}))} className={cls(errors.date_obtention, false)} />
                        <FieldError msg={getErr(errors, 'date_obtention')} />
                    </div>
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Pays d'obtention</label>
                    <select value={form.pays} onChange={e => setForm(p => ({...p, pays: e.target.value}))} className={cls(false, false)}>
                        {countryOptions.map(c => <option key={c.value} value={c.label}>{c.label}</option>)}
                    </select>
                </div>
                <button type="button" onClick={addDiplome} className="w-full py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1 transition">
                    <Plus className="h-4 w-4" /> Ajouter ce diplôme
                </button>
            </div>

            {diplomes.length > 0 && (
                <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-600">Diplômes ajoutés ({diplomes.length})</p>
                    {diplomes.map((d, i) => (
                        <div key={i} className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                            <div>
                                <p className="text-sm font-medium text-gray-800">{d._typeName}</p>
                                <p className="text-xs text-gray-500">{d.etablissement} — {d.date_obtention}</p>
                            </div>
                            <button onClick={() => onDiplomesChange(diplomes.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                        </div>
                    ))}
                </div>
            )}

            <div className="flex gap-2 pt-1">
                <button onClick={onBack} className="flex items-center gap-1 px-4 py-2.5 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition"><ChevronLeft className="h-4 w-4" /> Retour</button>
                <button onClick={handleNext} disabled={diplomes.length === 0} className="flex-1 flex justify-center items-center gap-2 py-2.5 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white rounded-lg text-sm font-medium transition">
                    Continuer ({diplomes.length} diplôme{diplomes.length > 1 ? 's' : ''}) <ChevronRight className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
};

export default StepDiplomes;
